/**
 * storage-proxy — a file a person opens or saves comes from our own address.
 *
 * The door (`app/v/[fileId]/route.ts`) hands out a pass: a signed Supabase
 * Storage URL on `<project>.supabase.co`. Followed as is, that host is what the
 * address bar and the save sheet name. The web app proxies Storage's two signed
 * reads on our origin instead: rewrites in next.config.ts, so the edge streams
 * the bytes (Range, Content-Type, the `download=` filename, ETag and Expires
 * pass through) and no function runs. A pass becomes the same signed path and
 * query on our origin; the token in its query stays the only key.
 *
 * Only signed reads are proxied: an object (`object/sign`) and an image
 * transform (`render/image/sign`). Nothing else of Storage is reachable here.
 * Imported by next.config.ts, so it imports nothing.
 */

/** A PDF: no sandbox, because a sandboxed document renders no PDF. */
export const STORAGE_PROXY_PATH = "/files/storage";
/** Everything else: sandboxed (STORAGE_PROXY_HEADER_GROUPS). */
export const STORAGE_PROXY_SANDBOXED_PATH = "/files/storage-s";

const STORAGE_API = "/storage/v1";
const SIGNED_READS = ["object/sign", "render/image/sign"] as const;

/**
 * On our origin a stored file is same-origin with the app. A document a
 * browser runs (an SVG, XHTML, XML) would run with the app's cookies, the
 * session the Supabase SDK keeps readable among them, and Storage demotes only
 * text/html. So no script runs on either path (inline styles still apply, an
 * SVG's own, and no form posts anywhere), and the type Storage declares is
 * binding (nosniff).
 *
 * A scriptless document still carries the app's cookies on what it loads: an
 * SVG's `<image href="/auth/reset">` signs the viewer out. The sandboxed path
 * makes the document an opaque origin, so what it loads goes without them, and
 * a meta refresh does not run. Pictures and clips opened on their own still
 * load (`img-src`/`media-src 'self'`), and a download still saves
 * (`allow-downloads`: WebKit refuses one in a sandboxed frame otherwise). A
 * PDF cannot take the sandbox, so its path loads nothing at all.
 *
 * `x-vercel-enable-rewrite-caching: 0`: Vercel's CDN caches an external
 * rewrite by the upstream's caching headers, and keeps a 206 as the answer to
 * every later request for that url (staging's /ingest proxy answered
 * bytes=200-299 with 0-99). A seek would get the wrong bytes, so every request
 * goes to Storage.
 */
const NO_SCRIPT =
  "default-src 'none'; style-src 'unsafe-inline'; form-action 'none'";
const proxyHeaders = (csp: string) => [
  { key: "Content-Security-Policy", value: csp },
  { key: "X-Content-Type-Options", value: "nosniff" },
  { key: "x-vercel-enable-rewrite-caching", value: "0" },
];
export const STORAGE_PROXY_HEADER_GROUPS = [
  {
    source: `${STORAGE_PROXY_PATH}/:path*`,
    headers: proxyHeaders(NO_SCRIPT),
  },
  {
    source: `${STORAGE_PROXY_SANDBOXED_PATH}/:path*`,
    headers: proxyHeaders(
      `${NO_SCRIPT}; img-src 'self' data:; media-src 'self'; sandbox allow-downloads`
    ),
  },
];

/** The project's origin, or null when the url is unset or not a url. */
function storageOrigin(supabaseUrl: string | undefined): string | null {
  if (!supabaseUrl) return null;
  try {
    return new URL(supabaseUrl).origin;
  } catch {
    return null;
  }
}

/** One rewrite per path and signed read, to this project's Storage; none without a project url. */
export function storageProxyRewrites(
  supabaseUrl: string | undefined
): { source: string; destination: string }[] {
  const origin = storageOrigin(supabaseUrl);
  if (!origin) return [];
  return [STORAGE_PROXY_PATH, STORAGE_PROXY_SANDBOXED_PATH].flatMap((path) =>
    SIGNED_READS.map((read) => ({
      source: `${path}/${read}/:path*`,
      destination: `${origin}${STORAGE_API}/${read}/:path*`,
    }))
  );
}

/**
 * A signed read of this project's Storage as the same path and query on our
 * origin (`/files/storage-s/object/sign/<bucket>/<path>?token=…`): a PDF's on
 * the unsandboxed path, anything else's on the sandboxed one. Null for any
 * other url: another host, a public object, a door, a blob.
 */
export function storageProxyPath(
  url: string,
  { pdf = false }: { pdf?: boolean } = {},
  supabaseUrl: string | undefined = process.env.NEXT_PUBLIC_SUPABASE_URL
): string | null {
  const origin = storageOrigin(supabaseUrl);
  if (!origin) return null;
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }
  if (parsed.origin !== origin) return null;
  const signed = SIGNED_READS.some((read) =>
    parsed.pathname.startsWith(`${STORAGE_API}/${read}/`)
  );
  if (!signed) return null;
  const path = pdf ? STORAGE_PROXY_PATH : STORAGE_PROXY_SANDBOXED_PATH;
  return `${path}${parsed.pathname.slice(STORAGE_API.length)}${parsed.search}`;
}
